import { S3_BUCKET_URL } from "src/constants";
import { IJob } from "src/models"
import { IJobListReportRequest, IReportRejectSelectionConfig } from "src/models/Report";
import { downloadImageToFs, downloadPdfToFs } from "src/utils/download";
import * as api from "../../api";
import { leaveBreadcrumb, logError } from "src/utils/tracker";
import { isElectron } from "src/utils/miscUtils";
import { isDoubleSidedScanner } from "src/models/UserSettings";
import { getDisplayableJobId } from "src/utils/jobUtils";
import { getCurrentLocalReportContext } from "src/utils/localReportContext";

const canUseElectronFileOps = () => {
    return isElectron() && typeof window !== 'undefined' && (window as any).electron?.copyLocalFile && (window as any).electron?.showSaveDialog;
};

const copyLocalFileWithPrompt = async (sourcePath: string, defaultName: string, filters: { name: string; extensions: string[] }[]) => {
    if (!canUseElectronFileOps()) {
        throw new Error('Electron file operations not available');
    }

    const destinationPath = await (window as any).electron.showSaveDialog({
        defaultPath: defaultName,
        filters
    });

    if (!destinationPath) {
        return false;
    }

    await (window as any).electron.copyLocalFile({
        sourcePath,
        destinationPath
    });
    return true;
};

const reportFileExtension = (format: 'pdf' | 'html' | 'json' | 'xls') => {
    return format === 'xls' ? 'xlsx' : format;
};

const reportFileFilters = (format: 'pdf' | 'html' | 'json' | 'xls') => {
    switch (format) {
        case 'pdf':
            return [{ name: 'PDF', extensions: ['pdf'] }];
        case 'html':
            return [{ name: 'HTML', extensions: ['html'] }];
        case 'json':
            return [{ name: 'JSON', extensions: ['json'] }];
        case 'xls':
            return [{ name: 'Excel', extensions: ['xlsx'] }];
        default:
            return [{ name: 'Report', extensions: [reportFileExtension(format)] }];
    }
};

const buildFreshReportRequestParams = (
    jobId: string,
    locale: string,
    format: 'pdf' | 'html' | 'json' | 'xls' | 'thermal_58mm',
    rejectionCalculationConfig?: IReportRejectSelectionConfig
) => {
    const reportRefreshToken = `${Date.now()}`;
    return {
        reportRefreshToken,
        reportParams: {
            jobId,
            deviceTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata",
            locale,
            output_format: format,
            rejectionCalculationConfig,
            forceRegenerate: true,
            reportRefreshToken
        }
    };
};

const appendCacheBuster = (url: string, token: string) => {
    try {
        const parsed = new URL(url);
        parsed.searchParams.set('reportRefreshToken', token);
        return parsed.toString();
    } catch {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}reportRefreshToken=${encodeURIComponent(token)}`;
    }
};

export const downloadScannedImageFromId = async (jobId: string) => {
    try {
        const job = await api.getJobUnified(jobId);
        await downloadScannedImage(job);
    } catch (error) {
        const errorToLog = error instanceof Error ? error : new Error('Download scanned image failed');
        logError(errorToLog, {
            context: 'download_scanned_image_from_id',
            jobId
        });
    }
}

export const downloadScannedImage = async (job: IJob | null | undefined) => {
    if (job === undefined || job === null) {
        return
    }

    try {
        if (job.processingMode === 'local' && canUseElectronFileOps()) {
            const sourcePaths = job.results?.local_files?.scanned_image_paths || job.results?.scanned_images || [];
            if (sourcePaths.length === 0) {
                throw new Error('Local scanned image path not found');
            }

            for (let i = 0; i < sourcePaths.length; i++) {
                const sourcePath = sourcePaths[i];
                const defaultName = sourcePaths.length > 1 ? `scan_${job.jobId}_${i + 1}.jpg` : `scan_${job.jobId}.jpg`;
                await copyLocalFileWithPrompt(sourcePath, defaultName, [{ name: 'Images', extensions: ['jpg', 'jpeg', 'png'] }]);
            }
            return;
        }

        if (job?.input_object_key === undefined) {
            return
        }

        if (isDoubleSidedScanner(job.additionalAttributes?.scannerId)) {
            const scannedImages = job.results?.scanned_images || [];

            if (scannedImages.length === 0) {
                leaveBreadcrumb('No scanned images found for double-sided scan', {
                    jobId: job.jobId
                }, 'log');
                return;
            }

            for (let i = 0; i < scannedImages.length; i++) {
                const scannedImage = scannedImages[i];
                await downloadImageToFs(`${S3_BUCKET_URL()}/${scannedImage}`, `${job?.jobId}-${i}`)
            }
        } else {
            await downloadImageToFs(`${S3_BUCKET_URL()}/${job?.input_object_key}`, job?.jobId)
        }
    } catch (error) {
        const errorToLog = error instanceof Error ? error : new Error('Download scanned image failed');
        logError(errorToLog, {
            context: 'download_scanned_image',
            jobId: job?.jobId
        });
        throw errorToLog;
    }
}

export const downloadLabeledImage = async (job: IJob | null | undefined) => {
    if (job === undefined
        || job === null
        || job.productName === "L10_GRAIN_ANALYSIS"
        || job.productName === "MANUAL_GRAIN_ANALYSIS") {
        return
    }
    if (job.processingMode !== 'local' && job?.input_object_key === undefined) {
        return
    }

    try {
        if (job.processingMode === 'local' && canUseElectronFileOps()) {
            const sourcePath = job.results?.local_files?.labeled_image_path || job.results?.labeled_image;
            if (!sourcePath) {
                throw new Error('Local labeled image path not found');
            }

            await copyLocalFileWithPrompt(
                sourcePath,
                `labeled_${job.jobId}.jpg`,
                [{ name: 'Images', extensions: ['jpg', 'jpeg', 'png'] }]
            );
            return;
        }

        await downloadImageToFs(`${S3_BUCKET_URL()}/${job?.results?.labeled_image}`, job?.jobId)
    } catch (error) {
        const errorToLog = error instanceof Error ? error : new Error('Download labeled image failed');
        logError(errorToLog, {
            context: 'download_labeled_image',
            jobId: job?.jobId
        });
        throw errorToLog;
    }
}

export const downloadReportPdf = async (
    job: IJob | null | undefined,
    format: 'pdf' | 'html' | 'json' | 'xls' = 'pdf',
    locale = 'en',
    rejectionCalculationConfig?: IReportRejectSelectionConfig
) => {
    if (job === undefined || job === null || job?.jobId === undefined) {
        return
    }

    try {
        const effectiveRejectionCalculationConfig = rejectionCalculationConfig
            || (job?.results?.rejectionCalculationConfig as IReportRejectSelectionConfig | undefined);
        if (job.processingMode === 'local' && canUseElectronFileOps()) {
            if (!(window as any).electron?.generateLocalReport) {
                throw new Error('Local report generation not available');
            }

            const response = await (window as any).electron.generateLocalReport({
                jobId: job.jobId,
                format,
                locale,
                reportContext: getCurrentLocalReportContext(),
                rejectionCalculationConfig: effectiveRejectionCalculationConfig
            });

            const outputPath = response?.outputPath as string | undefined;
            if (!outputPath) {
                throw new Error('Local report path not found');
            }

            const ext = reportFileExtension(format);
            const defaultName = `InstaGrainX_${getDisplayableJobId(job.jobId)}.${ext}`;
            await copyLocalFileWithPrompt(outputPath, defaultName, reportFileFilters(format));
            return;
        } else if (job.processingMode === 'local') {
            if (typeof window !== 'undefined') {
                window.alert('Report download for local jobs is only available in the desktop app.');
            }
            return;
        }

        const { reportParams, reportRefreshToken } = buildFreshReportRequestParams(
            job.jobId,
            locale,
            format,
            effectiveRejectionCalculationConfig
        );
        const response = await api.getReport({
            report_type: "job_report",
            report_params: reportParams
        });

        if (response?.report_url === undefined) {
            leaveBreadcrumb('Report download skipped: missing report URL', {
                jobId: job?.jobId,
                format
            }, 'log');
            return
        }

        await downloadPdfToFs(appendCacheBuster(response.report_url, reportRefreshToken), job?.jobId, format)
    } catch (error) {
        const errorToLog = error instanceof Error ? error : new Error('Download report failed');
        logError(errorToLog, {
            context: 'download_report',
            jobId: job?.jobId,
            format
        });
        throw errorToLog;
    }
}

export const downloadJobListReport = async (request: IJobListReportRequest) => {
    try {
        if (isElectron() && canUseElectronFileOps()) {
            if (!(window as any).electron?.generateLocalJobListReport) {
                throw new Error('Local job list report generation not available');
            }
            const response = await (window as any).electron.generateLocalJobListReport({
                scope: request.scope,
                filters: request.filters,
                locale: request.locale
            });
            const outputPath = response?.outputPath as string | undefined;
            if (!outputPath) {
                throw new Error('Local job list report path not found');
            }
            const date = new Date().toISOString().slice(0, 10);
            return await copyLocalFileWithPrompt(
                outputPath,
                `InstaGrainX_Job_History_${date}.xlsx`,
                [{ name: 'Excel', extensions: ['xlsx'] }]
            );
        }

        const response = await api.getJobListReport(request);
        if (!response?.report_url) {
            throw new Error('Job list report URL not returned');
        }
        await downloadPdfToFs(response.report_url, 'job_history', 'xls');
        return true;
    } catch (error) {
        const errorToLog = error instanceof Error ? error : new Error('Download job list report failed');
        logError(errorToLog, {
            context: 'download_job_list_report',
            scopeType: request.scope.type
        });
        throw errorToLog;
    }
}

export const downloadCsv = async (csvUrl: string, filename: string, job: IJob) => {
    try {
        if (job.processingMode === 'local' && canUseElectronFileOps()) {
            let sourcePath: string | undefined;

            if (csvUrl.includes('indiGrainResults')) {
                sourcePath = job.results?.local_files?.indi_grain_csv_path;
            } else if (csvUrl.includes('rejectionAnalysis')) {
                sourcePath = job.results?.local_files?.rejection_analysis_csv_path;
            } else if (csvUrl.includes('particleDistribution')) {
                sourcePath = job.results?.local_files?.particle_distribution_csv_path;
            } else if (csvUrl.includes('cumulativeResult')) {
                sourcePath = job.results?.local_files?.cumulative_analysis_csv_path;
            } else {
                sourcePath = job.results?.local_files?.rejection_analysis_display_csv_path;
            }

            if (!sourcePath) {
                sourcePath = csvUrl;
            }

            await copyLocalFileWithPrompt(
                sourcePath,
                filename,
                [{ name: 'CSV', extensions: ['csv'] }]
            );
            return;
        }

        const response = await fetch(csvUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        const errorToLog = error instanceof Error ? error : new Error('Download CSV failed');
        logError(errorToLog, {
            context: 'download_csv',
            jobId: job?.jobId
        });
        throw errorToLog;
    }
};
